import axios from "axios";
import { toast } from "react-toastify";

// API base URL
const API_BASE_URL = "https://pevbackend.codovio.com/public/api";

// Axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use(
  (config) => {
    // Check token expiry before request
    const expiryTime = localStorage.getItem("token_expiry");
    const currentTime = Date.now();

    if (expiryTime && currentTime >= parseInt(expiryTime, 10)) {
      localStorage.clear();

      toast.error("Your session has expired. Please sign in again.");
      setTimeout(() => window.location.replace("/sign-in"), 100);
      return Promise.reject(new Error("Session expired"));
    }

    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle 401 errors
// Response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Skip session expiration logic for login endpoint
    if (error.config?.url.includes("/login-client")) {
      return Promise.reject(error); // Let the login error propagate to the component
    }

    if (error.response?.status === 401) {
      localStorage.clear();
      toast.error("Your session has expired. Please sign in again.");
      // Use window.location for immediate redirect
      setTimeout(() => {
        window.location.replace("/sign-in");
      }, 100);
    }
    return Promise.reject(error);
  }
);

// Auth APIs
const authApi = {
  login: async (email, password) => {
    try {
      const response = await api.post("/login-client", {
        email,
        password,
        product_id: 1,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
   google_login: async (name,email,photo,latitude = null,longitude = null) => {
    try {
      const response = await api.post("/google-login", {
        name,
        email,
        photo,
        latitude,
        longitude
        // product_id: 1,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  sendOtp: async (email) => {
    try {
      const response = await api.post("/send-otp", { email });
      const data = response.data;

      // If API returns success: false, throw error with message
      if (data.success === false) {
        throw new Error(data.message || "Failed to send OTP");
      }

      return data;
    } catch (error) {
      // If it's our custom error, throw it directly
      if (error.message && !error.response) {
        throw error;
      }
      // Throw just the message instead of the entire response data
      throw new Error(
        error.response?.data?.message || error.message || "Failed to send OTP"
      );
    }
  },

  verifyOtp: async (otp) => {
    try {
      const response = await api.post("/verify-otp", { otp });
      const data = response.data;

      // If API returns success: false, throw error with message
      if (data.success === false) {
        throw new Error(data.message || "Invalid or expired OTP");
      }

      return data;
    } catch (error) {
      // If it's our custom error, throw it directly
      if (error.message && !error.response) {
        throw error;
      }
      throw error.response?.data || error.message;
    }
  },

  resetPassword: async (email, newPassword) => {
    try {
      const response = await api.post("/reset-password", {
        email,
        new_password: newPassword,
        product_id: 1,
      });
      const data = response.data;

      // If API returns success: false, throw error with message
      if (data.success === false) {
        throw new Error(data.message || "Failed to reset password");
      }

      return data;
    } catch (error) {
      // If it's our custom error, throw it directly
      if (error.message && !error.response) {
        throw error;
      }
      throw error.response?.data || error.message;
    }
  },

  clientSignup: async (formData) => {
    try {
      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };
      const response = await axios.post(
        `${API_BASE_URL}/client-signup`,
        formData,
        config
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  uploadImage: async (file) => {
    try {
      if (!file || !(file instanceof File)) {
        throw new Error("Invalid file provided for upload");
      }

      const formData = new FormData();
      formData.append("logo", file);

      const response = await api.post("/upload-image", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (!response.data?.tiny_url) {
        throw new Error("No tiny_url returned in response");
      }

      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  logoImage: async (file) => {
    try {
      if (!file || !(file instanceof File)) {
        throw new Error("Invalid file provided for upload");
      }

      const formData = new FormData();
      formData.append("logo", file);

      const response = await api.post("/upload-image", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (!response.data?.tiny_url) {
        throw new Error("No tiny_url returned in response");
      }
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  profileImage: async (file) => {
    try {
      if (!file || !(file instanceof File)) {
        throw new Error("Invalid file provided for upload");
      }

      const formData = new FormData();
      formData.append("profile_picture", file);

      const response = await api.post("/upload-image", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (!response.data?.tiny_url) {
        throw new Error("No tiny_url returned in response");
      }

      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  Image_Upload: async (file,type) => {
    try {
      if (!file || !(file instanceof File)) {
        throw new Error("Invalid file provided for upload");
      }

      const formData = new FormData();
      formData.append(type, file);

      const response = await api.post("/upload-image", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (!response.data?.tiny_url) {
        throw new Error("No tiny_url returned in response");
      }

      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

// Customer APIs
const customerApi = {
  getCustomerProfiles: async (params = {}) => {
    try {
      // const { page = 1, per_page = 10, search = "", status = "" } = params;
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);
      if (params.sort_by) queryParams.append("sort_by", params.sort_by);
      if (params.sort_order)
        queryParams.append("sort_order", params.sort_order);

      const response = await api.get(
        `/get-customer-profiles?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  addCustomerProfile: async (customerData) => {
    try {
      const response = await api.post("/add-customer-profile", customerData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  getCustomerProfileById: async (customerId) => {
    try {
      const response = await api.get(
        `/get-customer-profile-by-id/${customerId}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  updateCustomerProfile: async (customerId, customerData) => {
    try {
      const response = await api.put(
        `/update-customer-profile/${customerId}`,
        customerData
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  deleteCustomer: async (customerId) => {
    try {
      const response = await api.delete(
        `/delete-customer-profile/${customerId}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

// Branch APIs
const branchApi = {
  addClientBranch: async (branchData) => {
    try {
      const response = await api.post("/add-client-branch", branchData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  getClientBranches: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);
      if (params.sort_by) queryParams.append("sort_by", params.sort_by);
      if (params.sort_order)
        queryParams.append("sort_order", params.sort_order);
      if (params.want_pagination !== undefined)
        queryParams.append(
          "want_pagination",
          params.want_pagination ? "1" : "0"
        );
      // if (params.want_pagination) queryParams.append("want_pagination", 0);

      const url = queryParams.toString()
        ? `/get-client-branches?${queryParams.toString()}`
        : "/get-client-branches";

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getClientBranchById: async (branchId) => {
    try {
      const response = await api.get(`/get-client-branch-by-id/${branchId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  getSlotsByLocationId: async (params = {}) => {
    try {
      const { location_id, page = 1, per_page = 100, search = "" } = params;


      if (!location_id) {
        throw new Error("Location ID is required");
      }

      // Updated URL structure to match your curl example
      // Using location_id as path parameter instead of query parameter
      const url = `/get-slots-by-location-id/${location_id}`;

      // Add other query parameters if needed
      const queryParams = new URLSearchParams();
      // queryParams.append("page", page);
      // queryParams.append("per_page", per_page);
      if (search) queryParams.append("search", search);

      const finalUrl = queryParams.toString()
        ? `${url}?${queryParams.toString()}`
        : url;


      const response = await api.get(finalUrl);

      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  updateClientBranch: async (branchId, branchData) => {
    try {
      const response = await api.put(
        `/update-client-branch/${branchId}`,
        branchData
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  deleteBranch: async (branchId) => {
    try {
      const response = await api.delete(`/delete-client-branch/${branchId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  addBranchLocation: async (locationData) => {
    try {
      const response = await api.post("/add-branch-location", locationData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  getAllBranchLocations: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);
      if (params.sort_by) queryParams.append("sort_by", params.sort_by);
      if (params.sort_order)
        queryParams.append("sort_order", params.sort_order);
      if (params.want_pagination) queryParams.append("want_pagination", 0);
      const url = queryParams.toString()
        ? `/get-all-branch-locations?${queryParams.toString()}`
        : "/get-all-branch-locations";
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  deleteBranchLocation: async (locationId) => {
    try {
      const response = await api.delete(
        `/delete-branch-location/${locationId}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  updateBranchLocation: async (locationId, locationData) => {
    try {
      const response = await api.put(
        `/update-branch-location/${locationId}`,
        locationData
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  addParkingSlot: async (slotData) => {
    try {
      const response = await api.post("/add-parking-slot", slotData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  getAllParkingSlots: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);
      if (params.sort_by) queryParams.append("sort_by", params.sort_by);
      if (params.sort_order)
        queryParams.append("sort_order", params.sort_order);
      const url = queryParams
        ? `/get-all-parking-slots?${queryParams.toString()}`
        : "/get-all-parking-slots";
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  deleteParkingSlot: async (slotId) => {
    try {
      const response = await api.delete(`/delete-parking-slot/${slotId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  updateParkingSlot: async (slotId, slotData) => {
    try {
      const response = await api.put(
        `/update-parking-slot/${slotId}`,
        slotData
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  getAllVehicleCheckins: async () => {
    try {
      const response = await api.get("/get-all-vehicle-checkins");
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  checkInVehicle: async (checkInData) => {
    try {
      const formData = new FormData();

      // Required fields
      formData.append("slot_id", checkInData.slot_id);
      formData.append("valet_id", checkInData.valet_id);
      formData.append("plate_number", checkInData.plate_number);
      formData.append("plate_type", checkInData.plate_type);
      formData.append("make", checkInData.make);
      formData.append("model", checkInData.model);

      // Optional fields (only append if provided to avoid empty strings)
      if (checkInData.color) {
        formData.append("color", checkInData.color);
      }
      if (checkInData.notes) {
        formData.append("notes", checkInData.notes);
      }
      if (checkInData.condition_notes) {
        formData.append("condition_notes", checkInData.condition_notes);
      }

      // Handle is_walkin to match curl (true/false as strings)
      formData.append("is_walkin", checkInData.is_walkin ? "true" : "false");

      // Handle photos with checkin_photos[] syntax
      if (
        checkInData.checkin_photos &&
        Array.isArray(checkInData.checkin_photos)
      ) {
        checkInData.checkin_photos.forEach((photo) => {
          formData.append("checkin_photos[]", photo);
        });
      }

      // Handle walk-in or customer profile
      if (checkInData.is_walkin) {
        formData.append("walkin_name", checkInData.walkin_name);
        formData.append("walkin_phone", checkInData.walkin_phone);
      } else {
        formData.append("customer_id", checkInData.customer_id);
      }

      const response = await api.post("/checkin-vehicle", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          // Ensure Authorization header is included (modify based on your api setup)
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  checkoutVehicle: async (checkoutData) => {
    try {
      const formData = new FormData();
      Object.keys(checkoutData).forEach((key) => {
        if (key === "photos" && Array.isArray(checkoutData[key])) {
          checkoutData[key].forEach((photo) => {
            formData.append(`checkout_photos[]`, photo); // Use checkout_photos[] to match cURL
          });
        } else {
          formData.append(key, checkoutData[key]);
        }
      });

      const response = await api.post("/checkout-vehicle", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("access_token") || ""}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

// Location APIs with pagination
const locationApi = {
  getAllCountries: async (params = {}) => {
    const queryParams = new URLSearchParams({
      want_pagination: 0,
      sort_by: "country_name",
      sort_order: "asc",
    });
    const response = await api.get(
      `/get-all-countries?${queryParams.toString()}`
    );
    return response.data;
  },

  getAllCities: async ({ country_id }) => {
    const queryParams = new URLSearchParams({
      country_id,
      want_pagination: 0,
      sort_by: "city_name",
      sort_order: "asc",
    });
    const response = await api.get(
      `/get-cities-by-country-id?${queryParams.toString()}`
    );
    return response.data;
  },
  getTaxByCountry: async ({ country_id }) => {
    try {
      const queryParams = new URLSearchParams({
        country_id,
      });
      const response = await api.get(
        `/get-taxes-by-country?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

// Staff APIs
const staffApi = {
  getClientBranches: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);
      if (params.sort_by) queryParams.append("sort_by", params.sort_by);
      if (params.sort_order)
        queryParams.append("sort_order", params.sort_order);
      const url = queryParams.toString()
        ? `/get-client-branches?${queryParams.toString()}`
        : "/get-client-branches";
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Add this to your staffApi object
  getRevenueReport: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();

      // Format dates to YYYY-MM-DD
      if (params.from_date) {
        const fromDate = new Date(params.from_date);
        if (!isNaN(fromDate)) {
          queryParams.append("from_date", fromDate.toISOString().split("T")[0]);
        } else {
          throw new Error("Invalid from_date format");
        }
      }
      if (params.to_date) {
        const toDate = new Date(params.to_date);
        if (!isNaN(toDate)) {
          queryParams.append("to_date", toDate.toISOString().split("T")[0]);
        } else {
          throw new Error("Invalid to_date format");
        }
      }

      if (params.branch_id) queryParams.append("branch_id", params.branch_id);
      if (params.location_id)
        queryParams.append("location_id", params.location_id);
      // if (params.per_page) queryParams.append("per_page", params.per_page);
      // if (params.page) queryParams.append("page", params.page);
      // if (params.sort_by) queryParams.append("sort_by", params.sort_by);
      // if (params.sort_order)
      //   queryParams.append("sort_order", params.sort_order);

      const url = queryParams.toString()
        ? `/revenue-report?${queryParams.toString()}`
        : "/revenue-report";

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  getRevenueReportV2: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();

      // Format dates to YYYY-MM-DD
      if (params.from_date) {
        const fromDate = new Date(params.from_date);
        if (!isNaN(fromDate)) {
          queryParams.append("from_date", fromDate.toISOString().split("T")[0]);
        } else {
          throw new Error("Invalid from_date format");
        }
      }
      if (params.to_date) {
        const toDate = new Date(params.to_date);
        if (!isNaN(toDate)) {
          queryParams.append("to_date", toDate.toISOString().split("T")[0]);
        } else {
          throw new Error("Invalid to_date format");
        }
      }

      if (params.branch_id) queryParams.append("branch_id", params.branch_id);
      if (params.location_id)
        queryParams.append("location_id", params.location_id);
      const url = queryParams.toString()
        ? `/revenue-report-v2?${queryParams.toString()}`
        : "/revenue-report-v2";

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getAssignedPermissions: async () => {
    try {
      const response = await api.get("/get-all-assigned-permissions");
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getAllAssignedPermissions: async () => {
    try {
      const response = await api.get("/get-all-role-permissions");
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getStaffAttendanceReport: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.month) queryParams.append("month", params.month);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.page) queryParams.append("page", params.page);
      if (params.sort_by) queryParams.append("sort_by", params.sort_by);
      if (params.sort_order)
        queryParams.append("sort_order", params.sort_order);
      if (params.search) queryParams.append("search", params.search);
      if (params.staff_id) queryParams.append("staff_id", params.staff_id); // Add this line

      const url = queryParams.toString()
        ? `/staff-attendance-report?${queryParams.toString()}`
        : "/staff-attendance-report";

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  createIncentivePlan: async (planData) => {
    try {
      const response = await api.post("/create-incentive-plan", planData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  updateIncentivePlan: async (planId, planData) => {
    try {
      const response = await api.put(
        `/update-incentive-plan/${planId}`,
        planData
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Updated deleteIncentivePlan function in your API file (Api.jsx)

  deleteIncentivePlan: async (planId) => {
    try {

      // Validate planId
      if (!planId) {
        throw new Error("Plan ID is required");
      }

      // Check if it's a temporary ID (shouldn't reach here, but extra safety)
      if (String(planId).startsWith("temp_")) {
        throw new Error("Cannot delete plan with temporary ID");
      }

      const response = await api.delete(`/delete-incentive-plan/${planId}`);

      return response.data;
    } catch (error) {
     throw error.response?.data || error.message;
    }
  },

  submitRedeemRequest: async (redeemData) => {
    try {

      const response = await api.post("/submit-redeem-request", redeemData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getWalletTransactions: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.page) queryParams.append("page", params.page);
      if (params.sort_by) queryParams.append("sort_by", params.sort_by);
      if (params.sort_order)
        queryParams.append("sort_order", params.sort_order);
      if (params.search) queryParams.append("search", params.search);

      const url = queryParams.toString()
        ? `/wallet/transactions?${queryParams.toString()}`
        : "/wallet/transactions";

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getStaffRedeemRequests: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.status) queryParams.append("status", params.status);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.page) queryParams.append("page", params.page);

      const url = queryParams.toString()
        ? `/staff-redeem-requests?${queryParams.toString()}`
        : "/staff-redeem-requests";

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  processRedeemRequest: async (requestId, action) => {
    try {
      const response = await api.put(`/process-redeem-request/${requestId}`, {
        action,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getStaffWallet: async (staffId) => {
    try {
      const response = await api.get(`/staff-wallet/${staffId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getStaffById: async (staffId) => {
    try {
      const response = await api.get(`/get-staff/${staffId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  addStaff: async (staffData) => {
    try {
      const response = await api.post("/add-staff", staffData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  getAllStaff: async () => {
    try {
      const response = await api.get("/get-all-staff");
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  assignStaffToBranches: async (assignmentData) => {
    try {
      const response = await api.post("/assign-staff-branches", assignmentData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  getAssignedStaff: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);
      if (params.sort_by) queryParams.append("sort_by", params.sort_by);
      if (params.sort_order)
        queryParams.append("sort_order", params.sort_order);
      const url = queryParams.toString()
        ? `/get-assigned-staff?${queryParams.toString()}`
        : "/get-assigned-staff";

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  // Modified API function to accept query parameters
  getStaffByClient: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);
      if (params.sort_by) queryParams.append("sort_by", params.sort_by);
      if (params.sort_order)
        queryParams.append("sort_order", params.sort_order);
      if (params.want_pagination) queryParams.append("want_pagination", 0);
      const url = queryParams.toString()
        ? `/get-staff-by-client?${queryParams.toString()}`
        : "/get-staff-by-client";

      const response = await api.get(url);

      // Return the complete response data
      return response.data; // This should be { success, message, pagination, data }
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  updateStaff: async (staffId, staffData) => {
    try {
      const response = await api.put(`/update-staff/${staffId}`, staffData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  getStaffById: async (staffId) => {
    try {
      const response = await api.get(`/get-staff-by-id/${staffId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  deleteStaff: async (staffId) => {
    try {
      const response = await api.delete(`/delete-staff/${staffId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  deleteAssignedStaff: async (assignStaffId) => {
    try {
      const response = await api.delete(
        `/delete-assigned-staff/${assignStaffId}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  getAllClientRoles: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);
      if (params.sort_by) queryParams.append("sort_by", params.sort_by);
      if (params.sort_order)
        queryParams.append("sort_order", params.sort_order);
      const url = queryParams.toString()
        ? `/get-all-client-roles?${queryParams.toString()}`
        : "/get-all-client-roles";
      const response = await api.get(url);

      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  createClientRole: async (roleData) => {
    try {
      const response = await api.post("/create-client-role", roleData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  deleteClientRole: async (roleId) => {
    try {
      const response = await api.delete(`/delete-client-role/${roleId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  updateClientRole: async (roleId, roleData) => {
    try {
      const response = await api.put(`/update-client-role/${roleId}`, roleData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  getAllClientPermissions: async (params = {}) => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append("page", params.page);
    if (params.per_page) queryParams.append("per_page", params.per_page);
    if (params.search) queryParams.append("search", params.search);
    if (params.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params.sort_order) queryParams.append("sort_order", params.sort_order);
    if (params.want_pagination !== undefined)
      queryParams.append("want_pagination", params.want_pagination ? "1" : "0");
    try {
      const response = await api.get(
        `/get-all-client-permissions?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  createClientPermission: async (permissionData) => {
    try {
      const response = await api.post(
        "/create-client-permission",
        permissionData
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  assignRolePermissions: async (permissionData) => {
    try {
      const response = await api.post(
        "/assign-role-permissions",
        permissionData
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  getAllRolePermissions: async () => {
    try {
      const response = await api.get("/get-all-role-permissions");
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  deleteRolePermissions: async (data) => {
    try {
      const response = await api.delete("/delete-role-permission", { data });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  getAllIncentivePlans: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);
      if (params.sort_by) queryParams.append("sort_by", params.sort_by);
      if (params.sort_order)
        queryParams.append("sort_order", params.sort_order);

      const url = queryParams.toString()
        ? `/get-all-incentive-plans?${queryParams.toString()}`
        : "/get-all-incentive-plans";

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

// Client APIs
const clientApi = {
  getClientById: async (clientId) => {
    try {
      const response = await api.get(`/get-client-by-id/${clientId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  getClientPlanUsageHistory: async (params = {}) => {
    try {
      const {
        page = 1,
        per_page = 10,
        search = "",
        plan = "",
        status = "",
      } = params;
      const queryParams = new URLSearchParams();
      queryParams.append("page", page);
      queryParams.append("per_page", per_page);
      if (search) queryParams.append("search", search);
      if (plan) queryParams.append("plan", plan);
      if (status) queryParams.append("status", status);
      if (params.sort_by) queryParams.append("sort_by", params.sort_by);
      if (params.sort_order)
        queryParams.append("sort_order", params.sort_order);
      const response = await api.get(
        `/client-plan-usage-history?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

//workFlow
const workflowApi = {
  getCustomerDataByPhoneNumber: async (phoneNumber) => {
    try {
      const response = await api.get(
        `/get-customer-data-by-phone-no?phone_number=${phoneNumber}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  getAssignedWorkflowsV2: async (params = {}) => {
    try {
      const {
        page = 1,
        per_page = 10,
        search = "",
        sort_by = "workflow_id",
        sort_order = "asc",
      } = params;
      const queryParams = new URLSearchParams();
      queryParams.append("page", page);
      queryParams.append("per_page", per_page);
      if (search) queryParams.append("search", search);
      queryParams.append("sort_by", sort_by);
      queryParams.append("sort_order", sort_order);

      const response = await api.get(
        `/get-assigned-workflows-v2?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  getWorkflowsByLocation: async (locationId, params = {}) => {
    try {
      const {
        page = 1,
        per_page = 10,
        search = "",
        sort_by = "workflow_id",
        sort_order = "asc",
      } = params;

      const queryParams = new URLSearchParams();
      queryParams.append("page", page);
      queryParams.append("per_page", per_page);
      if (search) queryParams.append("search", search);
      queryParams.append("sort_by", sort_by);
      queryParams.append("sort_order", sort_order);

      const response = await api.get(
        `/locations/${locationId}/workflows?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getAllIncentives: async (params = {}) => {
    try {
      const {
        page = 1,
        limit = 10,
        search = "",
        sort_by = "assignment_id",
        sort_order = "asc",
      } = params;

      const queryParams = new URLSearchParams();
      queryParams.append("page", page);
      queryParams.append("limit", limit);
      if (search) queryParams.append("search", search);
      queryParams.append("sort_by", sort_by);
      queryParams.append("sort_order", sort_order);

      const response = await api.get(
        `/get-all-incentives?${queryParams.toString()}`
      );

      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  assignIncentive: async (assignmentData) => {
    try {
      if (
        !assignmentData.plan_id ||
        !assignmentData.branch_id ||
        !assignmentData.location_id ||
        !assignmentData.workflow_id
      ) {
        throw new Error(
          "Missing required fields: plan_id, branch_id, location_id, or workflow_id"
        );
      }

      const requestBody = {
        plan_id: parseInt(assignmentData.plan_id),
        branch_id: parseInt(assignmentData.branch_id),
        location_id: parseInt(assignmentData.location_id),
        workflow_id: parseInt(assignmentData.workflow_id),
        is_active:
          assignmentData.is_active !== undefined
            ? assignmentData.is_active
            : true,
        start_datetime:
          assignmentData.start_datetime || new Date().toISOString(),
        end_datetime: assignmentData.end_datetime || null,
      };


      const response = await api.post("/assign-incentive", requestBody, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  startWorkflow: async (workflowId, data = {}) => {
    try {
      if (!workflowId || workflowId === "undefined" || workflowId === "null") {
        throw new Error("Invalid workflow ID provided");
      }

      const numericWorkflowId = parseInt(workflowId);
      if (isNaN(numericWorkflowId)) {
        throw new Error(`Invalid workflow ID: ${workflowId}`);
      }

      const requestBody = {
        workflow_id: numericWorkflowId,
        ...data,
      };

      const response = await api.post(`/start-workflow`, requestBody, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  scanWorkflow: async (ticketNumber) => {
   try {
      const response = await api.get(
        `/fetch-current-step-by-ticket?ticket_number=${ticketNumber}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getWorkflowStep: async (instanceId) => {
    try {
      const response = await api.get(
        `/V2/workflow/step?instance_id=${instanceId}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  submitReceiveRequest: async (stepCode, instanceId, dataObj) => {
    try {
      if (!stepCode || !instanceId) {
        throw new Error("stepCode or instanceId is missing");
      }
      const response = await api.post(
        `/step/${instanceId}/submit/${stepCode}`,
        dataObj,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  uploadImage: async (file) => {
    try {
      if (!file || !(file instanceof File)) {
        throw new Error("Invalid file provided for upload");
      }

      const formData = new FormData();
      formData.append("checkin", file);

      const response = await api.post("/upload-image", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (!response.data?.tiny_url) {
        throw new Error("No tiny_url returned in response");
      }

      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  completeWorkflow: async (instanceId) => {
    try {
      if (!instanceId) {
        throw new Error("Instance ID is missing");
      }

      const response = await api.post(
        `/complete/${instanceId}/instance`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  getFieldOptions: async (optionsSource, params = {}) => {
    try {
      const {  search = "" } = params;

      // Check if the optionsSource already contains query parameters
      const separator = optionsSource.includes("?") ? "&" : "?";
      const queryParams = new URLSearchParams();

      // queryParams.append("page", page);
      // queryParams.append("per_page", per_page);
      if (search) queryParams.append("search", search);

      // Build the final URL - avoid double question marks
      const finalUrl = `${optionsSource} ${search && separator}${queryParams.toString()}`;

      const response = await api.get(finalUrl);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getBranchLocations: async (branchId, params = {}) => {
    try {
      const { search = "", per_page = 10, sort_by = "location_name" } = params;
      const queryParams = new URLSearchParams();

      if (search) queryParams.append("search", search);
      queryParams.append("per_page", per_page);
      queryParams.append("sort_by", sort_by);

      const response = await api.get(
        `/get-branch-locations/${branchId}?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getMyInstances: async (params = {}) => {
    try {
      const { page = 1, per_page = 10, search = "", status = "" } = params;
      const queryParams = new URLSearchParams();
      queryParams.append("page", page);
      queryParams.append("per_page", per_page);
      if (search) queryParams.append("search", search);
      if (status) queryParams.append("status", status);

      const response = await api.get(
        `/V2/get-my-instances?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  getInstanceHistory: async (params = {}) => {
    try {
      const { page = 1, per_page = 10, search = "", status = "" } = params;
      const queryParams = new URLSearchParams();
      queryParams.append("page", page);
      queryParams.append("per_page", per_page);
      if (search) queryParams.append("search", search);
      if (status) queryParams.append("status", status);

      const response = await api.get(
        `/get-instance-history?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

export {
  api,
  authApi,
  branchApi,
  locationApi,
  staffApi,
  API_BASE_URL,
  customerApi,
  clientApi,
  workflowApi,
};
